/**
 * evaClipMemory — once-per-session (or once-per-N-days) gating for Eva voice clips.
 *
 * Default storage: sessionStorage. Survives soft refresh in the same tab,
 * resets on new tab / new session.
 *
 * Opt-in localStorage with TTL via `markPlayed(id, { days: 1 })` for clips
 * that should stay silent across sessions for ~24h (e.g. modal opener).
 *
 * Safe in SSR / privacy-mode contexts (silently no-ops on storage errors).
 */

const SESSION_PREFIX = "eva_clip_played_v2:";
const LOCAL_PREFIX = "eva_clip_played_ttl_v2:";

type Opts = { days?: number };

function safeSession(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.sessionStorage;
  } catch { return null; }
}

function safeLocal(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage;
  } catch { return null; }
}

/** Build a voice-aware storage key. Falls back to "_" when no voiceId is supplied
 *  (preserves backwards-compatible call sites that don't care about voice). */
function makeKey(prefix: string, clipId: string, voiceId?: string): string {
  return prefix + (voiceId ?? "_") + ":" + clipId;
}

/** Returns true if the clip has already played in the current session
 *  (or, for TTL-marked clips, within its TTL window). */
export function hasPlayed(clipId: string, voiceId?: string): boolean {
  // Check localStorage TTL first (clips that opted into cross-session memory).
  const ls = safeLocal();
  if (ls) {
    try {
      const raw = ls.getItem(makeKey(LOCAL_PREFIX, clipId, voiceId));
      if (raw) {
        const exp = Number(raw);
        if (Number.isFinite(exp) && exp > Date.now()) return true;
        // Expired — clean up.
        ls.removeItem(makeKey(LOCAL_PREFIX, clipId, voiceId));
      }
    } catch {}
  }
  const ss = safeSession();
  if (!ss) return false;
  try { return ss.getItem(makeKey(SESSION_PREFIX, clipId, voiceId)) === "1"; } catch { return false; }
}

/** Mark a clip as played. With { days } it persists across sessions for that many days. */
export function markPlayed(clipId: string, voiceId?: string, opts?: Opts): void {
  const ss = safeSession();
  if (ss) {
    try { ss.setItem(makeKey(SESSION_PREFIX, clipId, voiceId), "1"); } catch {}
  }
  if (opts?.days && opts.days > 0) {
    const ls = safeLocal();
    if (ls) {
      try {
        const exp = Date.now() + opts.days * 24 * 60 * 60 * 1000;
        ls.setItem(makeKey(LOCAL_PREFIX, clipId, voiceId), String(exp));
      } catch {}
    }
  }
}

/** Wipe all gating (debug / testing helper). */
export function wipe(): void {
  const ss = safeSession();
  if (ss) {
    try {
      const toDel: string[] = [];
      for (let i = 0; i < ss.length; i++) {
        const k = ss.key(i);
        if (k && k.startsWith(SESSION_PREFIX)) toDel.push(k);
      }
      toDel.forEach(k => ss.removeItem(k));
    } catch {}
  }
  const ls = safeLocal();
  if (ls) {
    try {
      const toDel: string[] = [];
      for (let i = 0; i < ls.length; i++) {
        const k = ls.key(i);
        if (k && k.startsWith(LOCAL_PREFIX)) toDel.push(k);
      }
      toDel.forEach(k => ls.removeItem(k));
    } catch {}
  }
}